import { IconName } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isNumber } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Prompt, useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  createTaskLabelSettingLabelGroup,
  fetchTaskLabelSettingDetail,
  fetchTaskLabelSettingNewDetail,
  updateTaskLabelSettingLabelGroup,
} from '../../../../states/containers/task/taskSlice'
import { LangValue } from '../../../../states/types/common'
import {
  RequestPatchTaskLabelSettingLabelGroup,
  RequestPostTaskLabelSettingLabelGroup,
} from '../../../../states/types/task'
import { format, nextMonth, nowDate } from '../../../../utils/date'
import { replaceAndStrip } from '../../../../utils/string'
import { setPath, setUrlId } from '../../../../utils/url'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import AddColorModule from '../../../components/task/AddColorModule'
import AddLabelModal from '../../../components/task/AddLabelModal'
import AddTeamAndMemberModule from '../../../components/task/AddTeamAndMemberModule'
import DateCalendarModule from '../../../components/task/DateCalendarModule'
import DateModule from '../../../components/task/MonthCalendarModule'
import SetLabelModule from '../../../components/task/SetLabelModule'
import { URL } from '../../../constants/url'
import COLOR_LIST from '../../../constants/taskColor'
import VideoGuide from '../../../components/common/VideoGuide'
import MonthCalendarModal from '../../../components/task/MonthCalendarModal'

interface TemplateDetail {
  id?: number
  name: string
  color?: string
  code?: string
  description?: string
  permission: {
    viewable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
      category_managers?: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: {
    name: string
    auto_labelings?: {
      services: string[]
      keyword: {
        operator: string | null
        words: string[]
      }
      file_paths: string[]
      url: {
        pattern: string | null
        words: string[]
      }
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }[]
  }[]
}

interface AutoLabeling {
  id: number | null
  services: string[]
  keyword: {
    operator: string | null
    words: string[]
  }
  file_paths: string[]
  url: {
    pattern: string | null
    words: string[]
  }
  teams: {
    id: number
    name: string
  }[]
  members: {
    id: number
    name: string
  }[]
}

interface ModalState {
  label_group_id: number | null
  label_group_name: string
  label_id?: number | null
  label_name?: string
  label_code?: string | null
  label_color?: string
  label_master_id?: number | null
  auto_labelings?: AutoLabeling[]
  scheduled_time?: number // 想定工数(秒)
  type: string
}

type LocationState = {
  keep?: boolean;
  labelGroupId?: number;
  parentId?: number| null;
  detail?: TemplateDetail | null;
}

interface GroupedCost {
  start_date: string
  end_date: string | null
  list: {
    name: string
    value: string
  }[]
}

interface SettingList {
  start_date: string
  end_date: string
  name?: string
  value: string
}

const Detail: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()

  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const [inputGroupName, setInputGroupName] = useState<string>("")
  const [inputGroupCode, setInputGroupCode] = useState<string>("")
  const [inputScheduledStartDate, setInputScheduledStartDate] = useState<string>(nowDate("YYYY-MM-DD"))
  const [inputScheduledEndDate, setInputScheduledEndDate] = useState<string>(nowDate("YYYY-MM-DD"))
  const [inputColor, setInputColor] = useState<string>("#f2a2a2")
  // eslint-disable-next-line max-len
  const [selectUseTeams, setSelectUseTeams] = useState<{ id: number, origin_name: string, name: string, members: { id: number, name: string }[] }[]>([])
  const [selectUseMembers, setSelectUseMembers] = useState<{ id: number, name: string }[]>([])
  const [selectViewTeams, setSelectViewTeams] = useState<{ id: number, name: string, members: { id: number, name: string }[] }[]>([])
  const [selectViewMembers, setSelectViewMembers] = useState<{ id: number, name: string }[]>([])
  const [inputLabels, setInputLabels] = useState<ModalState[]>([])
  const [labelsError, setLabelsError] = useState<string>("")

  const [isInputSchedule, setIsInputSchedule] = useState<boolean>(true)

  const [isDisplayAddLabel, setIsDisplayAddLabel] = useState<boolean>(false)

  const [isDisplayScheduleArea, setIsDisplayScheduleArea] = useState<boolean>(true)

  const [isDisplayCostArea, setIsDisplayCostArea] = useState<boolean>(true)

  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [isSubmit, setIsSubmit] = useState<boolean>(false)

  const [inputAutoBudgets, setInputAutoBudgets] = useState<SettingList[]>([])
  const [inputAutoCosts, setInputAutoCosts] = useState<GroupedCost[]>([])
  const [inputAutoScheduledTimes, setInputAutoScheduledTimes] = useState<SettingList[]>([])

  const [openDateModalNum, setOpenDateModalNum] = useState<number>()

  const locationState = history.location.state as LocationState

  const modalStateInit = {
    label_group_id: null,
    label_group_name: "",
    label_name: "",
    type: "create",
  }

  const [modalState, setModalState] = useState<ModalState>(modalStateInit)

  const labelGroupDetail = useMemo(() => {
    if (!id && !task) return undefined
    return task.taskLabelSettingDetail?.label_group
  }, [id, task])

  const currentList = useMemo(() => {
    if (id) {
      const name = task.taskLabelSettingDetail?.label_group?.name || ""
      return task.taskLabelSettingDetail?.label_group_names.filter((v) => v !== name) || []
    }
    return task.taskLabelSettingDetail?.label_group_names || []
  }, [task, labelGroupDetail, id])

  const categoryViewMembers = useMemo(() => (
    locationState?.labelGroupId ? [] : labelGroupDetail?.permission.viewable.category_managers || []
  ), [labelGroupDetail])

  const title = useMemo(() => {
    if (id) {
      return "プロジェクト編集"
    }
    if (locationState?.labelGroupId) {
      return "プロジェクトのコピー"
    }
    return "新規プロジェクト作成"
  }, [id, locationState])

  const description = useMemo(() => {
    if (id) {
      return (
        <>
          <p>
            プロジェクトや業務を編集することができます。
          </p>
        </>
      )
    }
    if (locationState?.labelGroupId) {
      return (
        <>
          <p>
            既存のプロジェクトをコピーし、新たにプロジェクトを作成します。
          </p>
          <p>
            作成したプロジェクトは集計データによりコスト・時間・業務割合がそれぞれ可視化されます。
          </p>
        </>
      )
    }
    return (
      <>
        <p>
          プロジェクトを作成し、各種業務を設定できます。
        </p>
        <p>
          作成したプロジェクトは集計データによりコスト・時間・業務割合がそれぞれ可視化されます。
        </p>
      </>
    )
  }, [id, locationState])

  const tasks = useMemo(() => (
    [...task.taskLabelSettingDetail?.label_masters.filter((v) => !inputLabels.map((w) => w.label_name).includes(v.name)) || []]
  ), [task, inputLabels])

  const calcDiffMonth = (startDate: string, endDate: string | null) => {
    if (endDate) {
      return (Number(format(endDate, "MM")) - Number(format(startDate, "MM")))
        + ((Number(format(endDate, "YYYY")) - Number(format(startDate, "YYYY"))) * 12) + 1
    }
    if (Number(format(startDate, "YYYYMM")) <= Number(nowDate("YYYYMM"))) {
      return (Number(nowDate("MM")) - Number(format(startDate, "MM")))
        + ((Number(nowDate("YYYY")) - Number(format(startDate, "YYYY"))) * 12) + 1
    }
    return 0
  }

  const calcValue = (startDate: string, endDate: string | null, values: number[]) => {
    const tmpValue = values.reduce((s, w) => s + Number(w), 0)
    if (isNaN(tmpValue)) return "計算中…"
    return tmpValue * calcDiffMonth(startDate, endDate)
  }

  const totalBudgets = useMemo(() => {
    let total = 0
    let error = false
    inputAutoBudgets.forEach((budget) => {
      const tmpValue = Number(budget.value)
      if (isNaN(tmpValue)) {
        error = true
      }
      total += tmpValue * calcDiffMonth(budget.start_date, budget.end_date)
    })
    return error ? "計算中…" : total
  }, [inputAutoBudgets])

  const totalCost = useMemo(() => {
    let total = 0
    let error = false
    inputAutoCosts.forEach((cost) => {
      const tmpValue = cost.list.reduce((s, v) => s + Number(v.value), 0)
      if (isNaN(tmpValue)) {
        error = true
      }
      total += tmpValue * calcDiffMonth(cost.start_date, cost.end_date)
    })
    return error ? "計算中…" : total
  }, [inputAutoCosts])

  const totalScheduledTime = useMemo(() => {
    let totalSeconds = 0
    let error = false
    inputAutoScheduledTimes.forEach((scheduledTime) => {
      let tmpSeconds = 0
      const tmpArr = scheduledTime.value.split(":")
      if (isNaN(Number(tmpArr[0])) || (!!tmpArr[1] && isNaN(Number(tmpArr[1])))) {
        error = true
      } else {
        tmpSeconds += Number(tmpArr[0]) * 3600
        if (Number(tmpArr[1]) < 60) {
          tmpSeconds += Number(tmpArr[1]) * 60
        }
      }
      totalSeconds += tmpSeconds * calcDiffMonth(scheduledTime.start_date, scheduledTime.end_date)
    })
    return error ? "計算中…" : totalSeconds
  }, [inputAutoScheduledTimes])

  const budgetErrorList = useMemo(() => {
    const tmpErrorList: string[] = []
    inputAutoBudgets.forEach((v, i) => {
      const baseStartNum = Number(format(v.start_date, "YYYYMM"))
      const baseEndNum = Number(format(v.end_date, "YYYYMM"))
      if (Number(baseEndNum) - Number(baseStartNum) < 0) {
        tmpErrorList.push(`条件${i + 1}の指定範囲が正しくありません`)
      }
      inputAutoBudgets.filter((_, j) => j > i).forEach((w, j) => {
        const startNum = Number(format(w.start_date, "YYYYMM"))
        const endNum = Number(format(w.end_date, "YYYYMM"))
        if (!(baseStartNum > endNum || baseEndNum < startNum)) {
          tmpErrorList.push(`条件${i + 1}と条件${j + i + 2}の指定範囲が重複しています`)
        }
      })
    })
    return tmpErrorList
  }, [inputAutoBudgets])

  const costErrorList = useMemo(() => {
    const tmpErrorList: string[] = []
    inputAutoCosts.forEach((v, i) => {
      const baseStartNum = Number(format(v.start_date, "YYYYMM"))
      const baseEndNum = Number(format(v.end_date, "YYYYMM"))
      if (Number(baseEndNum) - Number(baseStartNum) < 0) {
        tmpErrorList.push(`条件${i + 1}の指定範囲が正しくありません`)
      }
    })
    return tmpErrorList
  }, [inputAutoCosts])

  const scheduleTimeErrorList = useMemo(() => {
    const tmpErrorList: string[] = []
    inputAutoScheduledTimes.forEach((v, i) => {
      const baseStartNum = Number(format(v.start_date, "YYYYMM"))
      const baseEndNum = Number(format(v.end_date, "YYYYMM"))
      if (Number(baseEndNum) - Number(baseStartNum) < 0) {
        tmpErrorList.push(`条件${i + 1}の指定範囲が正しくありません`)
      }
      inputAutoScheduledTimes.filter((_, j) => j > i).forEach((w, j) => {
        const startNum = Number(format(w.start_date, "YYYYMM"))
        const endNum = Number(format(w.end_date, "YYYYMM"))
        if (!(baseStartNum > endNum || baseEndNum < startNum)) {
          tmpErrorList.push(`条件${i + 1}と条件${j + i + 2}の指定範囲が重複しています`)
        }
      })
    })
    return tmpErrorList
  }, [inputAutoScheduledTimes])

  const timeToSec = (value: string) => {
    if (!value) return ""
    const tmpArr = value.split(":")
    if (isNaN(Number(tmpArr[1]))) {
      return (Number(tmpArr[0]) * 3600)
    }
    return (Number(tmpArr[0]) * 3600) + (Number(tmpArr[1]) * 60)
  }

  const clickDetail = (project_id: number) => {
    history.push(`${setPath(setUrlId(URL.TASK_LABEL_SETTING_DETAIL.path, 'id', project_id), "ja")}`)
  }

  const randomInt = (min: number, max: number) => {
    const intMin = Math.ceil(min)
    const intMax = Math.floor(max)
    return Math.floor(Math.random() * (intMax - intMin) + intMin)
  }

  const randomColor = () => COLOR_LIST.map((v) => v.values).flat()[randomInt(0, 23)].color

  const addLabelGroup = () => {
    setIsSubmit(true)
    setIsEdit(false)
    if (!id) {
      const tmpVal: RequestPostTaskLabelSettingLabelGroup = {
        name: inputGroupName,
        code: inputGroupCode || null,
        color: inputColor || "",
        scheduled_start_date: inputScheduledStartDate || "",
        scheduled_end_date: isInputSchedule ? inputScheduledEndDate : null,
        permission: {
          viewable: {
            team_ids: selectViewTeams.map((v) => v.id) || [],
            member_ids: selectViewMembers.map((v) => v.id) || [],
          },
          usable: {
            team_ids: selectUseTeams.map((v) => v.id) || [],
            member_ids: selectUseMembers.map((v) => v.id) || [],
          },
        },
        labels: inputLabels.filter((v) => v.label_name).map((v) => ({
          label_master_id: v.label_master_id || null,
          name: v.label_name || "",
          code: v.label_code || null,
          color: v.label_color || randomColor(),
          scheduled_start_date: null,
          scheduled_end_date: null,
          auto_labelings: v.auto_labelings?.map((auto_labeling) => (
            {
              services: auto_labeling.services || [],
              keyword: {
                operator: auto_labeling.keyword.words ? auto_labeling.keyword.operator : null,
                words: auto_labeling.keyword.words || [],
              },
              file_paths: auto_labeling.file_paths || [],
              url: {
                pattern: auto_labeling.url.words ? auto_labeling.url.pattern : null,
                words: auto_labeling.url.words || [],
              },
              teams: auto_labeling.teams.map((w) => w.id) || [],
              members: auto_labeling.members.map((w) => w.id) || [],
            }
          )) || [],
        })) || [],
        auto_scheduled_times: inputAutoScheduledTimes.map((v) => ({
          start_date: v.start_date,
          end_date: v.end_date || null,
          time: timeToSec(v.value) || 0,
        })) || [],
      }
      if (login.permissions?.task_project_cost_setting) {
        tmpVal.auto_budgets = inputAutoBudgets.map((v) => ({
          start_date: v.start_date,
          end_date: v.end_date || null,
          budget: Number(v.value) || 0,
        })) || []
        tmpVal.auto_other_costs = inputAutoCosts.map((v) => (
          v.list.map((w) => ({
            start_date: v.start_date,
            end_date: v.end_date || null,
            name: w.name,
            cost: Number(w.value),
          }))
        )).flat() || []
      }
      if (locationState?.detail?.id) {
        tmpVal.label_group_template_id = locationState.detail.id
      }
      dispatch(
        createTaskLabelSettingLabelGroup(
          tmpVal,
          () => {
            alert.success("プロジェクトを作成しました！")
            history.push(`${setPath(URL.TASK_LABEL_SETTING.path, language)}`)
          },
        ),
      )
    } else {
      const tmpVal: RequestPatchTaskLabelSettingLabelGroup = { id: id }
      if (labelGroupDetail?.name !== inputGroupName) {
        tmpVal.name = inputGroupName
      }
      if (labelGroupDetail?.code !== inputGroupCode) {
        tmpVal.code = inputGroupCode || null
      }
      if (inputColor && labelGroupDetail?.color !== inputColor) {
        tmpVal.color = inputColor
      }
      if (labelGroupDetail?.scheduled_start_date !== inputScheduledStartDate) {
        tmpVal.scheduled_start_date = inputScheduledStartDate
      }
      if ((labelGroupDetail?.scheduled_end_date !== inputScheduledEndDate) || (labelGroupDetail?.scheduled_end_date && !isInputSchedule)) {
        tmpVal.scheduled_end_date = isInputSchedule ? inputScheduledEndDate : null
      }
      tmpVal.permission = {
        viewable: {
          team_ids: selectViewTeams.map((v) => v.id) || [],
          member_ids: selectViewMembers.map((v) => v.id) || [],
        },
        usable: {
          team_ids: selectUseTeams.map((v) => v.id) || [],
          member_ids: selectUseMembers.map((v) => v.id) || [],
        },
      }
      tmpVal.labels = inputLabels.filter((v) => v.label_name).map((v) => ({
        label_id: v.label_id || null,
        label_master_id: v.label_master_id || null,
        name: v.label_name || "",
        code: v.label_code || null,
        color: v.label_color || randomColor(),
        auto_labelings: v.auto_labelings?.map((auto_labeling) => (
          {
            id: auto_labeling.id || null,
            services: auto_labeling.services || [],
            keyword: {
              operator: auto_labeling.keyword.words.length ? auto_labeling.keyword.operator : null,
              words: auto_labeling.keyword.words || [],
            },
            file_paths: auto_labeling.file_paths || [],
            url: {
              pattern: auto_labeling.url.words.length ? auto_labeling.url.pattern : null,
              words: auto_labeling.url.words || [],
            },
            teams: auto_labeling.teams.map((w) => w.id) || [],
            members: auto_labeling.members.map((w) => w.id) || [],
          }
        )) || [],
      }))
      if (login.permissions?.task_project_cost_setting) {
        tmpVal.auto_budgets = inputAutoBudgets.map((v) => ({
          start_date: v.start_date,
          end_date: v.end_date || null,
          budget: Number(v.value) || 0,
        })) || []
        tmpVal.auto_other_costs = inputAutoCosts.map((v) => (
          v.list.map((w) => ({
            start_date: v.start_date,
            end_date: v.end_date || null,
            name: w.name,
            cost: Number(w.value),
          }))
        )).flat() || []
      }
      tmpVal.auto_scheduled_times = inputAutoScheduledTimes.map((v) => ({
        start_date: v.start_date,
        end_date: v.end_date || null,
        time: timeToSec(v.value) || 0,
      })) || []
      dispatch(
        updateTaskLabelSettingLabelGroup(
          tmpVal,
          () => {
            alert.success("プロジェクトを編集しました！")
            if (locationState?.keep) {
              history.goBack()
            } else {
              history.push(`${setPath(URL.TASK_LABEL_SETTING.path, language)}`)
            }
          },
        ),
      )
    }
  }

  const addLabel = (params: ModalState) => {
    setIsEdit(true)
    if (params.type === "create") {
      setInputLabels([...inputLabels, params])
    } else {
      setInputLabels(inputLabels.map((v) => (v.label_name === params.label_name ? params : v)))
    }
    setIsDisplayAddLabel(false)
  }

  const openAddLabel = (labelName: string | null = null, create = true) => {
    setIsEdit(true)
    if (create) {
      setModalState(
        {
          label_group_id: labelGroupDetail?.id || null,
          label_group_name: inputGroupName || labelGroupDetail?.name || "",
          label_name: "",
          type: "create",
        },
      )
    } else {
      const label = inputLabels.find((v) => v.label_name === labelName)
      setModalState(
        {
          label_group_id: labelGroupDetail?.id || null,
          label_group_name: labelGroupDetail?.name || "",
          label_id: label?.label_id,
          label_name: label?.label_name,
          label_code: label?.label_code,
          label_color: label?.label_color,
          label_master_id: label?.label_master_id,
          type: "update",
          auto_labelings: label?.auto_labelings || [],
          scheduled_time: label?.scheduled_time,
        },
      )
    }
    setIsDisplayAddLabel(true)
  }

  const closeAddLabel = () => {
    setIsDisplayAddLabel(false)
    setModalState(modalStateInit)
  }

  const changeViewTeam = (teams: { id: number, name: string, members: { id: number, name: string }[] }[]) => {
    setIsEdit(true)
    setSelectViewTeams(teams)
  }

  const changeViewMember = (memberList: { id: number, name: string }[]) => {
    setIsEdit(true)
    setSelectViewMembers(memberList)
  }

  const changeUseTeam = (teams: { id: number, origin_name: string, name: string, members: { id: number, name: string }[] }[]) => {
    setIsEdit(true)
    setSelectUseTeams(teams)
    if (!login.permissions?.task_project_setting) return
    const addDiffTeams = teams.filter((v) => !selectUseTeams.map((w) => w.id).includes(v.id))
      .filter((v) => !selectViewTeams.map((w) => w.id).includes(v.id))
    if (!addDiffTeams) return
    setSelectViewTeams(
      [
        ...selectViewTeams,
        ...addDiffTeams.map((v) => ({
          id: v.id,
          name: `チーム「${v.origin_name}」のマネージャー`,
          members: task.taskLabelSettingDetail?.teams.find((w) => w.id === v.id)?.managers || [],
        })),
      ],
    )
  }

  const changeUseMember = (memberList: { id: number, name: string }[]) => {
    setIsEdit(true)
    setSelectUseMembers(memberList)
  }

  const changeInputColor = (value: string) => {
    setIsEdit(true)
    setInputColor(value)
  }

  const changeInputGroupName = (value: string) => {
    setIsEdit(true)
    setInputGroupName(value)
  }

  const changeInputGroupCode = (value: string) => {
    setIsEdit(true)
    setInputGroupCode(value)
  }

  const changeInputScheduledStartDate = (value: string) => {
    setIsEdit(true)
    setInputScheduledStartDate(value)
  }

  const changeInputScheduledEndDate = (value: string) => {
    setIsEdit(true)
    setInputScheduledEndDate(value)
  }

  const existsSameValue = (arr: string[]) => {
    const tmpArr = new Set(arr)
    return tmpArr.size !== arr.length
  }

  const addLabelList = () => {
    setIsEdit(true)
    setInputLabels([...inputLabels, modalStateInit])
  }

  const delLabelList = (num: number) => {
    setIsEdit(true)
    setInputLabels([...inputLabels.filter((_v, i) => i !== num)])
  }

  const changeLabelListCode = (num: number, code: string) => {
    setIsEdit(true)
    setInputLabels([...inputLabels.map((v, i) => (i === num ? { ...v, label_code: code } : v))])
  }

  const selectLabel = (labelName: string, num: number) => {
    setIsEdit(true)
    const param = tasks.find((v) => v.name === labelName)
    if (!param) {
      setInputLabels(
        [...inputLabels.map((v, i) => (i === num ? {
          ...modalStateInit,
          label_name: labelName,
        } : v))],
      )
      return
    }
    setInputLabels(
      [...inputLabels.map((v, i) => (i === num ? {
        ...modalStateInit,
        label_name: param.name,
        label_master_id: param.id,
        label_color: param.color,
      } : v))],
    )
  }

  const handleBeforeUnloadEvent = (event: BeforeUnloadEvent): void => {
    event.preventDefault()
    event.returnValue = "check"
  }

  const autoLabelSettingCount = (autoLabelings?: AutoLabeling[]) => {
    if (!autoLabelings?.length) return 0
    return autoLabelings.length
  }

  const formatNumber = (value: string) => {
    if (!value) return ""
    return Number(value.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0)).replace(/[^0-9]/g, ''))
  }

  const formatTime = (value: string) => {
    if (!value) return ""
    const tmpArr = value.split(":")
    const hour = formatNumber(tmpArr[0])
    const minute = formatNumber(tmpArr[1])
    if (!hour) return ""
    if (!minute || minute >= 60) return hour.toString()
    return `${hour}:${minute}`
  }

  const changeInputList = (item: string, type: string, value: string, index: number) => {
    setIsEdit(true)
    let setter
    if (item === "budget") {
      setter = setInputAutoBudgets
    } else if (item === "cost") {
      setter = setInputAutoCosts
    } else if (item === "scheduled_time") {
      setter = setInputAutoScheduledTimes
    }
    if (!setter) return
    if (type === "start_date") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setter((current: any[]) => current.map((v, i) => (i === index ? { ...v, start_date: value } : v)))
    } else if (type === "end_date") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setter((current: any[]) => current.map((v, i) => (i === index ? { ...v, end_date: value } : v)))
    } else if (type === "value") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setter((current: any[]) => current.map((v, i) => (i === index ? { ...v, value: value } : v)))
    }
    setOpenDateModalNum(undefined)
  }

  const changeInputDetailList = (item: string, type: string, value: string, index: number, detailIndex: number) => {
    setIsEdit(true)
    if (item === "cost") {
      if (type === "name") {
        setInputAutoCosts((current) => (
          current.map((v, i) => (i === index ? { ...v, list: v.list.map((w, j) => (j === detailIndex ? { ...w, name: value } : w)) } : v))
        ))
      }
      if (type === "value") {
        setInputAutoCosts((current) => (
          current.map((v, i) => (i === index ? { ...v, list: v.list.map((w, j) => (j === detailIndex ? { ...w, value: value } : w)) } : v))
        ))
      }
    }
  }

  const formatInputValue = (item: string, index: number) => {
    if (item === "budget") {
      setInputAutoBudgets((current) => current.map((v, i) => (i === index ? { ...v, value: formatNumber(v.value).toString() } : v)))
    } else if (item === "scheduled_time") {
      setInputAutoScheduledTimes((current) => current.map((v, i) => (i === index ? { ...v, value: formatTime(v.value) } : v)))
    }
  }

  const formatInputDetailValue = (item: string, index: number, detailIndex: number) => {
    if (item === "cost") {
      setInputAutoCosts((current) => (
        current.map((v, i) => (
          i === index ? { ...v, list: v.list.map((w, j) => (j === detailIndex ? { ...w, value: formatNumber(w.value).toString() } : w)) } : v
        ))
      ))
    }
  }

  const addList = (item: string) => {
    setIsEdit(true)
    if (item === "cost") {
      setInputAutoCosts((current) => [...current, { start_date: nowDate("YYYY-MM-01"), end_date: "", list: [] }])
      return
    }
    if (item === "budget") {
      if (inputAutoBudgets.length) {
        const tmpTarget = inputAutoBudgets.filter((v) => !v.end_date)
        if (tmpTarget.length) {
          setInputAutoBudgets((current) => [
            ...current.filter((v) => v.end_date),
            ...tmpTarget.map((v) => ({ ...v, end_date: format(v.start_date, "YYYY-MM-01") })),
            { start_date: nextMonth(tmpTarget[tmpTarget.length - 1].start_date, "YYYY-MM-01"), end_date: "", value: "" },
          ])
        } else {
          const tmpStartDate = [...inputAutoBudgets]
            .sort((a, b) => Number(format(b.end_date, "YYYYMM")) - Number(format(a.end_date, "YYYYMM")))[0].end_date || nowDate()
          setInputAutoBudgets((current) => [
            ...current,
            { start_date: nextMonth(tmpStartDate, "YYYY-MM-01"), end_date: "", value: "" },
          ])
        }
        return
      }
      setInputAutoBudgets((current) => [...current, { start_date: nowDate("YYYY-MM-01"), end_date: "", value: "" }])
    } else if (item === "scheduled_time") {
      if (inputAutoScheduledTimes.length) {
        const tmpTarget = inputAutoScheduledTimes.filter((v) => !v.end_date)
        if (tmpTarget.length) {
          setInputAutoScheduledTimes((current) => [
            ...current.filter((v) => v.end_date),
            ...tmpTarget.map((v) => ({ ...v, end_date: format(v.start_date, "YYYY-MM-01") })),
            { start_date: nextMonth(tmpTarget[tmpTarget.length - 1].start_date, "YYYY-MM-01"), end_date: "", value: "" },
          ])
        } else {
          const tmpStartDate = [...inputAutoScheduledTimes]
            .sort((a, b) => Number(format(b.end_date, "YYYYMM")) - Number(format(a.end_date, "YYYYMM")))[0].end_date || nowDate()
          setInputAutoScheduledTimes((current) => [
            ...current,
            { start_date: nextMonth(tmpStartDate, "YYYY-MM-01"), end_date: "", value: "" },
          ])
        }
        return
      }
      setInputAutoScheduledTimes((current) => [...current, { start_date: nowDate("YYYY-MM-01"), end_date: "", value: "" }])
    }
  }

  const delList = (item: string, index: number) => {
    setIsEdit(true)
    if (item === "budget") {
      setInputAutoBudgets((current) => [...current.filter((_v, i) => i !== index)])
    } else if (item === "cost") {
      setInputAutoCosts((current) => [...current.filter((_v, i) => i !== index)])
    } else if (item === "scheduled_time") {
      setInputAutoScheduledTimes((current) => [...current.filter((_v, i) => i !== index)])
    }
  }

  const addDetail = (item: string, index: number) => {
    if (item === "cost") {
      setInputAutoCosts((current) => current.map((v, i) => (i === index ? { ...v, list: [...v.list, { name: "", value: "" }] } : v)))
    }
  }

  const delDetail = (item: string, index: number, detailIndex: number) => {
    if (item === "cost") {
      setInputAutoCosts((current) => current.map((v, i) => (i === index ? { ...v, list: v.list.filter((_, j) => j !== detailIndex) } : v)))
    }
  }

  const buildTipsComment = (num: number) => {
    if (num) return `自動仕分け条件が${num}件設定されています`
    return "自動仕分け条件は設定されていません"
  }

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnloadEvent)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnloadEvent)
    }
  }, [])

  useEffect(() => {
    setInputGroupName(labelGroupDetail?.name || "")
    setInputGroupCode(labelGroupDetail?.code || "")
    setInputScheduledStartDate(format((labelGroupDetail?.scheduled_start_date || nowDate()), "YYYY-MM-DD"))
    setInputScheduledEndDate(format((labelGroupDetail?.scheduled_end_date || nowDate()), "YYYY-MM-DD"))
    setInputColor(labelGroupDetail?.color || randomColor())
    if (labelGroupDetail?.scheduled_end_date) {
      setIsInputSchedule(true)
    } else {
      setIsInputSchedule(false)
    }
    setSelectUseTeams(labelGroupDetail?.permission.usable.teams.map((v) => (
      {
        ...v,
        origin_name: v.name,
        name: `チーム「${v.name}」のメンバー`,
        members: task.taskLabelSettingDetail?.teams.find((w) => w.id === v.id)?.members || [],
      }
    )) || [])
    setSelectUseMembers(labelGroupDetail?.permission.usable.members || [])
    setSelectViewTeams(labelGroupDetail?.permission.viewable.teams.map((v) => (
      {
        id: v.id,
        name: `チーム「${v.name}」のマネージャー`,
        members: task.taskLabelSettingDetail?.teams.find((w) => w.id === v.id)?.managers || [],
      }
    )) || [])
    setSelectViewMembers(labelGroupDetail?.permission.viewable.members || [])
    setInputLabels(
      labelGroupDetail?.labels.map((v) => (
        {
          label_group_id: labelGroupDetail.id,
          label_group_name: labelGroupDetail.name,
          label_id: v.id,
          label_name: v.name,
          label_code: v.code,
          label_color: v.color,
          label_master_id: v.label_master_id,
          auto_labelings: v.auto_labelings.map((auto_labeling) => (
            {
              id: auto_labeling.id,
              services: auto_labeling.services || [],
              keyword: {
                operator: auto_labeling.keyword.words.length ? auto_labeling.keyword.operator : "or",
                words: auto_labeling.keyword.words || [],
              },
              file_paths: auto_labeling.file_paths || [],
              url: {
                pattern: auto_labeling.url.words.length ? auto_labeling.url.pattern : "partial",
                words: auto_labeling.url.words || [],
              },
              teams: auto_labeling.teams || [],
              members: auto_labeling.members || [],
            }
          )),
          type: "update",
        }
      )) || [],
    )
    setInputAutoBudgets(labelGroupDetail?.auto_budgets.map((v) => ({
      start_date: v.start_date,
      end_date: v.end_date || "",
      value: v.budget.toString(),
    })) || [])
    setInputAutoCosts(() => {
      let tmp: GroupedCost[] = []
      labelGroupDetail?.auto_other_costs.forEach((v) => {
        const target = tmp.find((w) => w.start_date === v.start_date && w.end_date === v.end_date)
        if (target) {
          tmp = tmp.map((w) => {
            if (w.start_date === v.start_date && w.end_date === v.end_date) {
              return { ...w, list: [...w.list, { name: v.name || "", value: v.cost.toString() }] }
            }
            return w
          })
        } else {
          tmp.push({
            start_date: v.start_date,
            end_date: v.end_date,
            list: [
              {
                name: v.name || "",
                value: v.cost.toString(),
              },
            ],
          })
        }
      })
      return tmp
    })
    setInputAutoScheduledTimes(labelGroupDetail?.auto_scheduled_times.map((v) => ({
      start_date: v.start_date,
      end_date: v.end_date || "",
      value: v.time % 3600 ? `${Math.floor(v.time / 3600)}:${(v.time % 3600) / 60}` : (v.time / 3600).toString(),
    })) || [])
  }, [labelGroupDetail])

  useEffect(() => {
    if (existsSameValue(inputLabels.filter((v) => v.label_name).map((v) => v.label_name || ""))) {
      setLabelsError("業務が重複しています。異なる業務を設定してください。")
    } else {
      setLabelsError("")
    }
  }, [inputLabels])

  useEffect(() => {
    if (id) {
      dispatch(fetchTaskLabelSettingDetail({ id: Number(id) }))
    } else if (locationState?.labelGroupId) {
      dispatch(fetchTaskLabelSettingDetail({ id: locationState.labelGroupId }))
    } else {
      dispatch(fetchTaskLabelSettingNewDetail())
    }
  }, [id, locationState])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!task.taskLabelSettingDetail || task.taskLabelSettingDetail?.label_group?.labels.length) return
    if (locationState && locationState.detail) {
      setInputColor(locationState.detail.color || randomColor())
      setInputGroupCode(locationState.detail.code || "")
      setSelectUseTeams(locationState.detail.permission.usable.teams.map((v) => (
        {
          ...v,
          origin_name: v.name,
          name: `チーム「${v.name}」のメンバー`,
          members: task.taskLabelSettingDetail?.teams.find((w) => w.id === v.id)?.members || [],
        }
      )) || [])
      setSelectUseMembers(locationState.detail.permission.usable.members || [])
      setSelectViewTeams(locationState.detail.permission.viewable.teams.map((v) => (
        {
          id: v.id,
          name: `チーム「${v.name}」のマネージャー`,
          members: task.taskLabelSettingDetail?.teams.find((w) => w.id === v.id)?.managers || [],
        }
      )) || [])
      setSelectViewMembers(locationState.detail.permission.viewable.members || [])
      setInputLabels(locationState.detail.labels.map((v) => ({
        ...modalStateInit,
        label_name: v.name,
        label_master_id: task.taskLabelSettingDetail?.label_masters.find((w) => w.name === v.name)?.id || null,
        label_color: task.taskLabelSettingDetail?.label_masters.find((w) => w.name === v.name)?.color || "",
        auto_labelings: v.auto_labelings?.map((auto_labeling) => (
          {
            id: null,
            services: auto_labeling.services || [],
            keyword: {
              operator: auto_labeling.keyword.words.length ? auto_labeling.keyword.operator : "or",
              words: auto_labeling.keyword.words || [],
            },
            file_paths: auto_labeling.file_paths || [],
            url: {
              pattern: auto_labeling.url.words.length ? auto_labeling.url.pattern : "partial",
              words: auto_labeling.url.words || [],
            },
            teams: auto_labeling.teams || [],
            members: auto_labeling.members || [],
          }
        )) || [],
      })))
      return
    }
    setInputLabels([{
      ...modalStateInit,
      label_name: "その他",
      label_master_id: task.taskLabelSettingDetail?.label_masters.find((v) => v.name === "その他")?.id,
      label_color: task.taskLabelSettingDetail?.label_masters.find((v) => v.name === "その他")?.color,
    }])
  }, [task.taskLabelSettingDetail])

  return (
    <section className="task_page task_status">
      <Helmet>
        <title>{`Qasee - 工数管理 - 設定 - プロジェクト設定 - ${title}`}</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.TASK_LABEL_SETTING.path, language)}
        >
          プロジェクト設定
        </Link>
        &nbsp;&gt;&nbsp;
        {title}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {title}
          <VideoGuide
            videoId="WO0IBdSDVQw"
          />
        </h1>
      </section>
      <section className="page_description">
        {description}
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    プロジェクトを読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  <div className="project_input_area">
                    {
                      id ? (
                        <div
                          style={{
                            marginBottom: "16px",
                            textAlign: "right",
                            color: "#666666",
                            fontSize: "14px",
                          }}
                        >
                          このプロジェクトは
                          <span
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                            }}
                          >
                            {labelGroupDetail?.created_by}
                          </span>
                          さんが
                          {format(labelGroupDetail?.created_at, "YYYY年MM月DD日")}
                          に作成しました
                        </div>
                      ) : (
                        <></>
                      )
                    }
                    <fieldset>
                      <legend>
                        概要
                      </legend>
                      <div className="item w20">
                        <div className="title">
                          プロジェクトカラー
                        </div>
                        {
                          login.permissions?.task_project_setting ? (
                            <div className="detail equal_space row_1">
                              <AddColorModule
                                color={inputColor}
                                changeCallback={(color) => changeInputColor(color)}
                              />
                            </div>
                          ) : (
                            <Tips
                              description="編集権限がありません"
                              className=""
                              icon={false}
                            >
                              <>
                                <div className="detail equal_space row_1">
                                  <div
                                    style={{
                                      height: "auto",
                                      width: "100%",
                                      padding: "16px",
                                      backgroundColor: "#EEEEEE",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      fontSize: "14px",
                                      display: "flex",
                                      gap: "8px",
                                      color: "#999999",
                                      cursor: "not-allowed",
                                      minHeight: "50px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        borderRadius: "50%",
                                        backgroundColor: inputColor || "#EEEEEE",
                                      }}
                                    />
                                    {inputColor}
                                  </div>
                                </div>
                              </>
                            </Tips>
                          )
                        }
                      </div>
                      <div className="item w50">
                        <div className="title">
                          プロジェクト
                          {
                            login.permissions?.task_project_setting && (
                              <div className="required">必須</div>
                            )
                          }
                        </div>
                        {
                          login.permissions?.task_project_setting ? (
                            <div className="detail equal_space row_1">
                              <input
                                type="text"
                                value={inputGroupName}
                                onChange={(e) => changeInputGroupName(e.target.value)}
                                onBlur={() => changeInputGroupName(replaceAndStrip(inputGroupName))}
                                placeholder="プロジェクトを入力"
                              />
                            </div>
                          ) : (
                            <Tips
                              description="編集権限がありません"
                              className=""
                              icon={false}
                            >
                              <>
                                <div className="detail equal_space row_1">
                                  <div
                                    style={{
                                      height: "auto",
                                      width: "100%",
                                      padding: "16px",
                                      backgroundColor: "#EEEEEE",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      fontSize: "14px",
                                      display: "flex",
                                      gap: "8px",
                                      color: "#999999",
                                      cursor: "not-allowed",
                                      minHeight: "50px",
                                    }}
                                  >
                                    {inputGroupName}
                                  </div>
                                </div>
                              </>
                            </Tips>
                          )
                        }
                        {
                          !inputGroupName.length && (
                            <div className="error">
                              プロジェクトを入力してください
                            </div>
                          )
                        }
                        {
                          currentList.includes(inputGroupName) && (
                            <>
                              <div className="error">
                                プロジェクト名が重複しています。
                              </div>
                              {
                                !locationState?.labelGroupId && (
                                  <div className="comment">
                                    <span className="strong">
                                      {task.taskLabelSetting?.label_groups.find((v) => v.name === inputGroupName)?.created_by}
                                    </span>
                                    さんが作成したプロジェクト
                                    <span
                                      className="strong link"
                                      onClick={() => clickDetail(task.taskLabelSetting?.label_groups.find((v) => v.name === inputGroupName)?.id || 0)}
                                      role="none"
                                    >
                                      {inputGroupName}
                                    </span>
                                    の編集をお試しください
                                  </div>
                                )
                              }
                            </>
                          )
                        }
                      </div>
                      <div className="item w30">
                        <div className="title">
                          プロジェクトコード
                        </div>
                        {
                          login.permissions?.task_project_setting ? (
                            <div className="detail equal_space row_1">
                              <input
                                type="text"
                                value={inputGroupCode}
                                placeholder="プロジェクトコードを入力(任意)"
                                onChange={(e) => changeInputGroupCode(e.target.value)}
                                onBlur={() => changeInputGroupCode(replaceAndStrip(inputGroupCode))}
                              />
                            </div>
                          ) : (
                            <Tips
                              description="編集権限がありません"
                              className=""
                              icon={false}
                            >
                              <>
                                <div className="detail equal_space row_1">
                                  <div
                                    style={{
                                      height: "auto",
                                      width: "100%",
                                      padding: "16px",
                                      backgroundColor: "#EEEEEE",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      fontSize: "14px",
                                      display: "flex",
                                      gap: "8px",
                                      color: "#999999",
                                      cursor: "not-allowed",
                                      minHeight: "50px",
                                    }}
                                  >
                                    {inputGroupCode}
                                  </div>
                                </div>
                              </>
                            </Tips>
                          )
                        }
                      </div>
                    </fieldset>
                    <fieldset>
                      <legend>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Tips
                            description="追加された表示チーム・メンバーは、このプロジェクトレポートと、設定された参加チーム・メンバーの業務仕分け画面が閲覧可能になります。"
                            className=""
                          >
                            <>
                              プロジェクト表示チーム・メンバー
                            </>
                          </Tips>
                          {
                            login.permissions?.task_project_setting && (
                              <div className="required">必須</div>
                            )
                          }
                        </div>
                      </legend>
                      <div className="item w100">
                        <div className="detail equal_space row_1">
                          <div
                            className="team_area"
                            style={{
                              backgroundColor: login.permissions?.task_project_setting ? "#FFFFFF" : "#EEEEEE",
                            }}
                          >
                            {
                              !!categoryViewMembers.length && (
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      padding: "8px",
                                      borderBottom: "solid 1px #CCCCCC",
                                      backgroundColor: "#EEEEEE",
                                    }}
                                  >
                                    <div>
                                      <Tips
                                        description="プロジェクトが所属しているカテゴリーの表示メンバーです。削除することはできません。"
                                        className=""
                                      >
                                        <>
                                          カテゴリー表示メンバー
                                        </>
                                      </Tips>
                                    </div>
                                  </div>
                                  <div
                                    className="chip_area"
                                    style={{
                                      padding: "8px",
                                    }}
                                  >
                                    {
                                      categoryViewMembers.map((member) => (
                                        <div
                                          className="chip"
                                          key={`category_manager_${member.id}`}
                                        >
                                          {member.name}
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              )
                            }
                            {
                              selectViewTeams.map((team) => (
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      padding: "8px",
                                      borderBottom: "solid 1px #CCCCCC",
                                      backgroundColor: "#EEEEEE",
                                    }}
                                  >
                                    <div>
                                      {team.name}
                                    </div>
                                    <div>
                                      {
                                        labelGroupDetail?.disabled || !login.permissions?.task_project_setting ? (
                                          <></>
                                        ) : (
                                          <span
                                            className="delete"
                                            onClick={() => changeViewTeam([...selectViewTeams.filter((v) => v.id !== team.id)])}
                                            role="none"
                                            style={{
                                              cursor: "pointer",
                                            }}
                                          >
                                            <FontAwesomeIcon icon={['fas', 'times']} />
                                          </span>
                                        )
                                      }
                                    </div>
                                  </div>
                                  <div
                                    className="chip_area"
                                    style={{
                                      padding: "8px",
                                    }}
                                  >
                                    {
                                      team.members.map((member) => (
                                        <div
                                          className="chip"
                                          key={`member_${member.id}`}
                                        >
                                          {member.name}
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              ))
                            }
                            <div className="chip_area">
                              {
                                selectViewMembers.map((member) => (
                                  <div
                                    className="chip"
                                    key={`member_${member.id}`}
                                  >
                                    {member.name}
                                    {
                                      labelGroupDetail?.disabled || !login.permissions?.task_project_setting ? (
                                        <></>
                                      ) : (
                                        <span
                                          className="delete"
                                          onClick={() => changeViewMember([...selectViewMembers.filter((v) => v.id !== member.id)])}
                                          role="none"
                                        >
                                          <FontAwesomeIcon icon={['fas', 'times']} />
                                        </span>
                                      )
                                    }
                                  </div>
                                ))
                              }
                            </div>
                            <div className="chip_area">
                              {
                                labelGroupDetail?.disabled || !login.permissions?.task_project_setting ? (
                                  <></>
                                ) : (
                                  <AddTeamAndMemberModule
                                    teamIdList={selectViewTeams.map((v) => v.id)}
                                    callbackTeams={(teams) => changeViewTeam(teams)}
                                    teamList={
                                      task.taskLabelSettingDetail?.teams.map((v) => (
                                        { ...v, origin_name: v.name, name: `チーム「${v.name}」のマネージャー`, members: v.managers }
                                      )) || []
                                    }
                                    memberIdList={selectViewMembers.map((v) => v.id)}
                                    callbackMembers={(memberList) => changeViewMember(memberList)}
                                    memberList={
                                      task.taskLabelSettingDetail?.members.map((v) => ({ id: v.id, name: v.name })) || []
                                    }
                                  />
                                )
                              }
                            </div>
                          </div>
                        </div>
                        {
                          (!selectViewTeams.length && !selectViewMembers.length) && (
                            <div className="error">
                              プロジェクト表示チーム・メンバーが設定されていません
                            </div>
                          )
                        }
                      </div>
                    </fieldset>
                    <fieldset>
                      <legend>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Tips
                            description="追加された参加チーム・メンバーは、このプロジェクトが業務仕分けの対象として業務が表示されるようになります。"
                            className=""
                          >
                            <>
                              プロジェクト参加チーム・メンバー
                            </>
                          </Tips>
                          <div className="required">必須</div>
                        </div>
                      </legend>
                      <div className="item w100">
                        <div className="detail equal_space row_1">
                          <div className="team_area">
                            {
                              selectUseTeams.map((team) => (
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      padding: "8px",
                                      borderBottom: "solid 1px #CCCCCC",
                                      backgroundColor: "#EEEEEE",
                                    }}
                                  >
                                    <div>
                                      {team.name}
                                    </div>
                                    <div>
                                      {
                                        labelGroupDetail?.disabled ? (
                                          <></>
                                        ) : (
                                          <span
                                            className="delete"
                                            // eslint-disable-next-line max-len
                                            onClick={() => changeUseTeam([...selectUseTeams.filter((v) => v.id !== team.id).map((v) => ({ ...v, origin_name: v.origin_name }))])}
                                            role="none"
                                            style={{
                                              cursor: "pointer",
                                            }}
                                          >
                                            <FontAwesomeIcon icon={['fas', 'times']} />
                                          </span>
                                        )
                                      }
                                    </div>
                                  </div>
                                  <div
                                    className="chip_area"
                                    style={{
                                      padding: "8px",
                                    }}
                                  >
                                    {
                                      team.members.map((member) => (
                                        <div
                                          className="chip"
                                          key={`member_${member.id}`}
                                        >
                                          {member.name}
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              ))
                            }
                            <div className="chip_area">
                              {
                                selectUseMembers.map((member) => (
                                  <div
                                    className="chip"
                                    key={`member_${member.id}`}
                                  >
                                    {member.name}
                                    {
                                      labelGroupDetail?.disabled ? (
                                        <></>
                                      ) : (
                                        <span
                                          className="delete"
                                          onClick={() => changeUseMember([...selectUseMembers.filter((v) => v.id !== member.id)])}
                                          role="none"
                                        >
                                          <FontAwesomeIcon icon={['fas', 'times']} />
                                        </span>
                                      )
                                    }
                                  </div>
                                ))
                              }
                            </div>
                            <div className="chip_area">
                              {
                                labelGroupDetail?.disabled ? (
                                  <></>
                                ) : (
                                  <AddTeamAndMemberModule
                                    teamIdList={selectUseTeams.map((v) => v.id)}
                                    callbackTeams={(teams) => changeUseTeam(teams.map((v) => ({ ...v, origin_name: v.origin_name })))}
                                    teamList={
                                      task.taskLabelSettingDetail?.teams.map((v) => (
                                        { ...v, origin_name: v.name, name: `チーム「${v.name}」のメンバー` }
                                      )) || []
                                    }
                                    memberIdList={selectUseMembers.map((v) => v.id)}
                                    callbackMembers={(memberList) => changeUseMember(memberList)}
                                    memberList={
                                      task.taskLabelSettingDetail?.members.map((v) => ({ id: v.id, name: v.name })) || []
                                    }
                                  />
                                )
                              }
                            </div>
                          </div>
                        </div>
                        {
                          (!selectUseTeams.length && !selectUseMembers.length) && (
                            <div className="error">
                              プロジェクト参加チーム・メンバーが設定されていません
                            </div>
                          )
                        }
                      </div>
                    </fieldset>
                    {
                      login.permissions?.task_project_setting && (
                        <>
                          <fieldset>
                            <legend>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                業務
                                {
                                  login.permissions?.task_project_setting && (
                                    <div className="required">必須</div>
                                  )
                                }
                              </div>
                            </legend>
                            <div className="item w100">
                              <div
                                className="detail row_1"
                                style={{
                                  display: "flex",
                                  flexFlow: "column",
                                  gap: "8px",
                                }}
                              >
                                {
                                  inputLabels.map((inputLabel, i) => (
                                    <div
                                      key={`job_${i.toString()}_${autoLabelSettingCount(inputLabel.auto_labelings)}`}
                                    >
                                      <div className="label_area">
                                        {
                                          login.permissions?.task_project_setting ? (
                                            <>
                                              <SetLabelModule
                                                currentLabelName={inputLabel.label_name || null}
                                                callbackLabel={(labelName) => selectLabel(labelName, i)}
                                                labelList={tasks}
                                                existLabel={!!inputLabel.label_master_id}
                                              />
                                              <input
                                                type="text"
                                                value={inputLabel.label_code || ""}
                                                placeholder="業務コードを入力(任意)"
                                                onChange={(e) => changeLabelListCode(i, e.target.value)}
                                                onBlur={() => changeLabelListCode(i, replaceAndStrip(inputLabels[i].label_code || ""))}
                                              />
                                              <Tips
                                                description={buildTipsComment(autoLabelSettingCount(inputLabel.auto_labelings))}
                                                className="del"
                                                icon={false}
                                              >
                                                <button
                                                  type="button"
                                                  className="advanced_setting"
                                                  onClick={() => openAddLabel(inputLabel.label_name, false)}
                                                  disabled={!inputLabel.label_name}
                                                >
                                                  自動仕分け設定
                                                </button>
                                              </Tips>
                                              <Tips
                                                description="業務を削除"
                                                className="del"
                                                icon={false}
                                              >
                                                <button
                                                  type="button"
                                                  onClick={() => delLabelList(i)}
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                                </button>
                                              </Tips>
                                            </>
                                          ) : (
                                            <>
                                              <div className="item w50" style={{ margin: 0 }}>
                                                <Tips
                                                  description="編集権限がありません"
                                                  className=""
                                                  icon={false}
                                                >
                                                  <>
                                                    <div
                                                      style={{
                                                        height: "auto",
                                                        width: "100%",
                                                        padding: "16px",
                                                        backgroundColor: "#EEEEEE",
                                                        border: "solid 1px #CCCCCC",
                                                        borderRadius: "4px",
                                                        fontSize: "14px",
                                                        display: "flex",
                                                        gap: "8px",
                                                        color: "#999999",
                                                        cursor: "not-allowed",
                                                        minHeight: "50px",
                                                      }}
                                                    >
                                                      {inputLabel.label_name || ""}
                                                    </div>
                                                  </>
                                                </Tips>
                                              </div>
                                              <div className="item w50" style={{ margin: 0 }}>
                                                <Tips
                                                  description="編集権限がありません"
                                                  className=""
                                                  icon={false}
                                                >
                                                  <>
                                                    <div
                                                      style={{
                                                        height: "auto",
                                                        width: "100%",
                                                        padding: "16px",
                                                        backgroundColor: "#EEEEEE",
                                                        border: "solid 1px #CCCCCC",
                                                        borderRadius: "4px",
                                                        fontSize: "14px",
                                                        display: "flex",
                                                        gap: "8px",
                                                        color: "#999999",
                                                        cursor: "not-allowed",
                                                        minHeight: "50px",
                                                      }}
                                                    >
                                                      {inputLabel.label_code || ""}
                                                    </div>
                                                  </>
                                                </Tips>
                                              </div>
                                            </>
                                          )
                                        }
                                      </div>
                                      {
                                        autoLabelSettingCount(inputLabel.auto_labelings) ? (
                                          <div
                                            style={{
                                              padding: "0 8px 8px",
                                              color: "#666666",
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "8px",
                                              border: "solid 1px #CCC",
                                              borderTop: "none",
                                              borderRadius: "0 0 4px 4px",
                                              wordBreak: "break-all",
                                              marginTop: "8px",
                                            }}
                                          >
                                            自動仕分け条件：
                                            {
                                              inputLabel.auto_labelings?.map((auto_labeling, j) => (
                                                <div
                                                  key={`auto_labeling_${j + 0}_${auto_labeling.id}`}
                                                  style={{
                                                    border: autoLabelSettingCount(inputLabel.auto_labelings) > 1 ? "solid 1px #CCCCCC" : "none",
                                                    borderRadius: "4px",
                                                    padding: "8px",
                                                    display: "flex",
                                                    flexFlow: "column",
                                                    gap: "8px",
                                                  }}
                                                >
                                                  {autoLabelSettingCount(inputLabel.auto_labelings) > 1 ? `条件${j + 1}` : ""}
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      flexFlow: "column",
                                                      gap: "8px",
                                                    }}
                                                  >
                                                    {
                                                      auto_labeling?.services.length ? (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: "4px",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              padding: "2px 4px",
                                                              backgroundColor: "#999999",
                                                              color: "#FFFFFF",
                                                              borderRadius: "4px",
                                                              whiteSpace: "nowrap",
                                                            }}
                                                          >
                                                            アプリケーション
                                                          </div>
                                                          <div
                                                            style={{
                                                              marginTop: "0.2em",
                                                            }}
                                                          >
                                                            {auto_labeling.services.join(',')}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <></>
                                                      )
                                                    }
                                                    {
                                                      auto_labeling?.teams.length || auto_labeling?.members.length ? (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: "4px",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              padding: "2px 4px",
                                                              backgroundColor: "#999999",
                                                              color: "#FFFFFF",
                                                              borderRadius: "4px",
                                                              whiteSpace: "nowrap",
                                                            }}
                                                          >
                                                            チーム・メンバー
                                                          </div>
                                                          <div
                                                            style={{
                                                              marginTop: "0.2em",
                                                            }}
                                                          >
                                                            {
                                                              [
                                                                ...auto_labeling.teams.map((v) => v.name),
                                                                ...auto_labeling.members.map((v) => v.name),
                                                              ].join(',')
                                                            }
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <></>
                                                      )
                                                    }
                                                    {
                                                      auto_labeling?.keyword.words.length ? (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: "4px",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              padding: "2px 4px",
                                                              backgroundColor: "#999999",
                                                              color: "#FFFFFF",
                                                              borderRadius: "4px",
                                                              whiteSpace: "nowrap",
                                                            }}
                                                          >
                                                            キーワード
                                                          </div>
                                                          <div
                                                            style={{
                                                              marginTop: "0.2em",
                                                            }}
                                                          >
                                                            {`【${auto_labeling.keyword.operator === "or" ? "いずれかを含む" : "全てを含む"}】`}
                                                            {auto_labeling.keyword.words.join(',')}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <></>
                                                      )
                                                    }
                                                    {
                                                      auto_labeling?.url.words.length ? (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: "4px",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              padding: "2px 4px",
                                                              backgroundColor: "#999999",
                                                              color: "#FFFFFF",
                                                              borderRadius: "4px",
                                                              whiteSpace: "nowrap",
                                                            }}
                                                          >
                                                            URL
                                                          </div>
                                                          <div
                                                            style={{
                                                              marginTop: "0.2em",
                                                            }}
                                                          >
                                                            {`【${auto_labeling.url.pattern === "exact" ? "完全一致" : "部分一致"}】`}
                                                            {auto_labeling.url.words.join(',')}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <></>
                                                      )
                                                    }
                                                    {
                                                      auto_labeling?.file_paths.length ? (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: "4px",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              padding: "2px 4px",
                                                              backgroundColor: "#999999",
                                                              color: "#FFFFFF",
                                                              borderRadius: "4px",
                                                              whiteSpace: "nowrap",
                                                            }}
                                                          >
                                                            ファイルパス
                                                          </div>
                                                          <div
                                                            style={{
                                                              marginTop: "0.2em",
                                                            }}
                                                          >
                                                            {auto_labeling.file_paths.join(',')}
                                                          </div>
                                                        </div>
                                                      ) : (
                                                        <></>
                                                      )
                                                    }
                                                  </div>
                                                </div>
                                              ))
                                            }
                                          </div>
                                        ) : (
                                          <></>
                                        )
                                      }
                                    </div>
                                  ))
                                }
                                {
                                  labelsError && (
                                    <div
                                      style={{
                                        color: "#FF0000",
                                        margin: "4px 0",
                                      }}
                                    >
                                      {labelsError}
                                    </div>
                                  )
                                }
                                {
                                  login.permissions?.task_project_setting && (
                                    <div className="add_area">
                                      <button
                                        type="button"
                                        onClick={addLabelList}
                                      >
                                        <FontAwesomeIcon icon={['fas', 'plus']} />
                                        &nbsp;
                                        業務追加
                                      </button>
                                    </div>
                                  )
                                }
                              </div>
                              {
                                !inputLabels.filter((v) => v.label_name).length && (
                                  <div className="error">
                                    業務が設定されていません
                                  </div>
                                )
                              }
                            </div>
                          </fieldset>
                          <fieldset className={!isDisplayScheduleArea ? "no_check" : ""}>
                            <div
                              className="legend"
                              onClick={() => setIsDisplayScheduleArea(!isDisplayScheduleArea)}
                              role="none"
                            >
                              期間
                            </div>
                            {
                              isDisplayScheduleArea && (
                                <>
                                  <div className="item w50">
                                    <div className="title">
                                      開始予定
                                      {
                                        login.permissions?.task_project_setting && (
                                          <div className="required">必須</div>
                                        )
                                      }
                                    </div>
                                    {
                                      login.permissions?.task_project_setting ? (
                                        <div className="detail equal_space row_1">
                                          <DateCalendarModule
                                            date={inputScheduledStartDate}
                                            changeCallback={(callback_date) => changeInputScheduledStartDate(callback_date)}
                                            omit
                                            future
                                            disabled={false}
                                          />
                                        </div>
                                      ) : (
                                        <Tips
                                          description="編集権限がありません"
                                          className=""
                                          icon={false}
                                        >
                                          <>
                                            <div
                                              style={{
                                                height: "auto",
                                                width: "100%",
                                                padding: "16px",
                                                backgroundColor: "#EEEEEE",
                                                border: "solid 1px #CCCCCC",
                                                borderRadius: "4px",
                                                fontSize: "14px",
                                                display: "flex",
                                                gap: "8px",
                                                color: "#999999",
                                                cursor: "not-allowed",
                                                minHeight: "50px",
                                              }}
                                            >
                                              {format(inputScheduledStartDate, "YYYY年MM月DD日")}
                                            </div>
                                          </>
                                        </Tips>
                                      )
                                    }
                                  </div>
                                  <div className="item w50">
                                    <div
                                      className="title"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      終了予定
                                      {
                                        login.permissions?.task_project_setting && (
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "4px",
                                              fontSize: "10px",
                                              fontWeight: "initial",
                                              cursor: "pointer",
                                            }}
                                            onClick={() => setIsInputSchedule(!isInputSchedule)}
                                            role="none"
                                          >
                                            <FontAwesomeIcon
                                              icon={['far', `${isInputSchedule ? 'check-square' : 'square'}` as IconName]}
                                              className={`icon ${isInputSchedule ? "checked" : "no_check"}`}
                                              style={{
                                                fontSize: "12px",
                                                color: isInputSchedule ? "#08984C" : "#666666",
                                              }}
                                            />
                                            <div className="check_title">
                                              設定する
                                            </div>
                                          </div>
                                        )
                                      }
                                    </div>
                                    {
                                      login.permissions?.task_project_setting ? (
                                        <div className="detail equal_space row_1">
                                          {
                                            isInputSchedule ? (
                                              <DateCalendarModule
                                                date={inputScheduledEndDate}
                                                changeCallback={(callback_date) => changeInputScheduledEndDate(callback_date)}
                                                omit
                                                future
                                                disabled={false}
                                              />
                                            ) : (
                                              <div
                                                onClick={() => setIsInputSchedule(true)}
                                                role="none"
                                              >
                                                終了予定設定なし
                                                <span className="link">
                                                  終了を設定
                                                </span>
                                              </div>
                                            )
                                          }
                                        </div>
                                      ) : (
                                        <Tips
                                          description="編集権限がありません"
                                          className=""
                                          icon={false}
                                        >
                                          <>
                                            <div
                                              style={{
                                                height: "auto",
                                                width: "100%",
                                                padding: "16px",
                                                backgroundColor: "#EEEEEE",
                                                border: "solid 1px #CCCCCC",
                                                borderRadius: "4px",
                                                fontSize: "14px",
                                                display: "flex",
                                                gap: "8px",
                                                color: "#999999",
                                                cursor: "not-allowed",
                                                minHeight: "50px",
                                              }}
                                            >
                                              {inputScheduledEndDate ? format(inputScheduledEndDate, "YYYY年MM月DD日") : "終了予定設定なし"}
                                            </div>
                                          </>
                                        </Tips>
                                      )
                                    }
                                  </div>
                                </>
                              )
                            }
                          </fieldset>
                          <fieldset className={!isDisplayCostArea ? "no_check" : ""}>
                            {
                              !login.permissions?.task_project_cost_setting
                              && login.permissions?.task_cost_view
                              && (!!inputAutoBudgets.length || !!inputAutoCosts.length)
                                ? (
                                  <div
                                    className="legend"
                                  >
                                    予算・その他のコスト・予定
                                  </div>
                                ) : (
                                  <div
                                    className="legend"
                                    onClick={() => setIsDisplayCostArea(!isDisplayCostArea)}
                                    role="none"
                                  >
                                    {
                                      (login.permissions?.task_project_cost_setting || login.permissions?.task_cost_view) ? (
                                        <>
                                          予算・その他のコスト・予定
                                        </>
                                      ) : (
                                        <>
                                          予定
                                        </>
                                      )
                                    }
                                  </div>
                                )
                            }
                            {
                              isDisplayCostArea && (
                                <>
                                  {
                                    login.permissions?.task_project_cost_setting && (
                                      <>
                                        <div className="item w100">
                                          <div
                                            className="title"
                                            style={{
                                              justifyContent: "space-between",
                                              marginBottom: inputAutoBudgets.length ? "16px" : "0",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                              }}
                                            >
                                              売上・予算(円)
                                            </div>
                                            <div>
                                              {`${nowDate("YYYY年MM月DD日")}時点で確定している金額 計：`}
                                              {isNumber(totalBudgets) ? `${totalBudgets.toLocaleString()}円` : totalBudgets}
                                            </div>
                                          </div>
                                          <div
                                            className="detail row_1"
                                            style={{
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "8px",
                                            }}
                                          >
                                            {
                                              inputAutoBudgets.map((v, i) => (
                                                <div
                                                  key={`budget_${i.toString()}`}
                                                  style={{
                                                    position: "relative",
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    marginBottom: "8px",
                                                    marginTop: 0,
                                                    gap: "8px",
                                                    border: "solid 1px #999999",
                                                    borderRadius: "4px",
                                                    padding: "16px 8px",
                                                  }}
                                                >
                                                  <DateModule
                                                    date={v.start_date}
                                                    changeCallback={(callback_date) => changeInputList("budget", "start_date", callback_date, i)}
                                                    noLimit
                                                    navigation={false}
                                                  />
                                                  <div>
                                                    ~
                                                  </div>
                                                  <DateModule
                                                    date={v.end_date}
                                                    changeCallback={(callback_date) => changeInputList("budget", "end_date", callback_date, i)}
                                                    noLimit
                                                    navigation={false}
                                                    empty
                                                  />
                                                  <input
                                                    type="text"
                                                    value={v.value || ""}
                                                    placeholder="予算(円)を入力"
                                                    onChange={(e) => changeInputList("budget", "value", e.target.value, i)}
                                                    onBlur={() => formatInputValue("budget", i)}
                                                    onKeyPress={(e) => e.key === "Enter" && formatInputValue("budget", i)}
                                                    style={{
                                                      padding: "12px 16px",
                                                      textAlign: "right",
                                                    }}
                                                  />
                                                  <Tips
                                                    description="予算を削除"
                                                    className="del"
                                                    icon={false}
                                                  >
                                                    <button
                                                      type="button"
                                                      onClick={() => delList("budget", i)}
                                                    >
                                                      <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                                    </button>
                                                  </Tips>
                                                  <div
                                                    style={{
                                                      position: "absolute",
                                                      top: "-8px",
                                                      right: "50px",
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "0 8px",
                                                      fontSize: "10px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      {
                                                        v.end_date ? (
                                                          <>
                                                            {`期間:${calcDiffMonth(v.start_date, v.end_date)}ヶ月`}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {`期間未確定(確定分:${calcDiffMonth(v.start_date, v.end_date)}ヶ月)`}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                    <div>
                                                      {
                                                        isNumber(calcValue(v.start_date, v.end_date, [Number(v.value)])) ? (
                                                          <>
                                                            {`計:${calcValue(v.start_date, v.end_date, [Number(v.value)]).toLocaleString()}円`}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {calcValue(v.start_date, v.end_date, [Number(v.value)])}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                </div>
                                              ))
                                            }
                                            {
                                              budgetErrorList.map((v) => (
                                                <div
                                                  className="error"
                                                  style={{
                                                    margin: "4px 0px",
                                                  }}
                                                >
                                                  {v}
                                                </div>
                                              ))
                                            }
                                            <div className="add_area">
                                              <button
                                                type="button"
                                                onClick={() => addList("budget")}
                                              >
                                                <FontAwesomeIcon icon={['fas', 'plus']} />
                                                &nbsp;
                                                予算追加
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="item w100">
                                          <div
                                            className="title"
                                            style={{
                                              justifyContent: "space-between",
                                              marginBottom: inputAutoCosts.length ? "16px" : "0",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                              }}
                                            >
                                              その他のコスト(円)
                                            </div>
                                            <div>
                                              {`${nowDate("YYYY年MM月DD日")}時点で確定している金額 計：`}
                                              {isNumber(totalCost) ? `${totalCost.toLocaleString()}円` : totalCost}
                                            </div>
                                          </div>
                                          <div
                                            className="detail row_1"
                                            style={{
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "24px",
                                            }}
                                          >
                                            {
                                              inputAutoCosts.map((v, i) => (
                                                <div
                                                  key={`cost_${i.toString()}`}
                                                  style={{
                                                    position: "relative",
                                                    width: "100%",
                                                    display: "flex",
                                                    flexFlow: "column",
                                                    gap: "4px",
                                                    border: "solid 1px #999999",
                                                    borderRadius: "4px",
                                                    padding: "16px 8px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "8px",
                                                      position: "absolute",
                                                      top: "-14px",
                                                      left: "8px",
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "0 4px",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        width: "120px",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                      }}
                                                    >
                                                      <button
                                                        type="button"
                                                        onClick={() => setOpenDateModalNum(i)}
                                                        style={{
                                                          position: "relative",
                                                          border: "solid 1px #999999",
                                                          borderRadius: "4px",
                                                          width: "100%",
                                                          padding: "4px",
                                                          textAlign: "center",
                                                          fontSize: "14px",
                                                        }}
                                                      >
                                                        {format(v.start_date, "YYYY年MM月")}
                                                        <FontAwesomeIcon
                                                          icon={['fas', 'angle-down']}
                                                          style={{
                                                            position: "absolute",
                                                            right: "8px",
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                          }}
                                                        />
                                                      </button>
                                                      {
                                                        openDateModalNum === i && (
                                                          <div
                                                            style={{
                                                              position: "relative",
                                                            }}
                                                          >
                                                            <MonthCalendarModal
                                                              date={v.start_date}
                                                              changeCallback={
                                                                (callback_date) => changeInputList("cost", "start_date", callback_date, i)
                                                              }
                                                              closeCallback={() => setOpenDateModalNum(undefined)}
                                                              noLimit
                                                            />
                                                          </div>
                                                        )
                                                      }
                                                    </div>
                                                    <div>
                                                      ~
                                                    </div>
                                                    <div
                                                      style={{
                                                        width: "120px",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                      }}
                                                    >
                                                      <button
                                                        type="button"
                                                        onClick={() => setOpenDateModalNum(i + 10000)}
                                                        style={{
                                                          position: "relative",
                                                          border: "solid 1px #999999",
                                                          borderRadius: "4px",
                                                          width: "100%",
                                                          padding: "4px",
                                                          textAlign: "center",
                                                          fontSize: "14px",
                                                        }}
                                                      >
                                                        {v.end_date ? format(v.end_date, "YYYY年MM月") : "指定なし"}
                                                        <FontAwesomeIcon
                                                          icon={['fas', 'angle-down']}
                                                          style={{
                                                            position: "absolute",
                                                            right: "8px",
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                          }}
                                                        />
                                                      </button>
                                                      {
                                                        openDateModalNum === i + 10000 && (
                                                          <div
                                                            style={{
                                                              position: "relative",
                                                            }}
                                                          >
                                                            <MonthCalendarModal
                                                              date={v.end_date || ""}
                                                              changeCallback={(callback_date) => {
                                                                changeInputList("cost", "end_date", callback_date, i)
                                                              }}
                                                              closeCallback={() => setOpenDateModalNum(undefined)}
                                                              noLimit
                                                              empty
                                                            />
                                                          </div>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                  <div
                                                    style={{
                                                      position: "absolute",
                                                      top: "-8px",
                                                      right: "50px",
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "0 8px",
                                                      fontSize: "10px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      {
                                                        v.end_date ? (
                                                          <>
                                                            {`期間:${calcDiffMonth(v.start_date, v.end_date)}ヶ月`}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {`期間未確定(確定分:${calcDiffMonth(v.start_date, v.end_date)}ヶ月)`}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                    <div>
                                                      {
                                                        isNumber(calcValue(v.start_date, v.end_date, v.list.map((w) => Number(w.value)))) ? (
                                                          <>
                                                            {/* eslint-disable-next-line max-len */}
                                                            {`計:${calcValue(v.start_date, v.end_date, v.list.map((w) => Number(w.value))).toLocaleString()}円`}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {calcValue(v.start_date, v.end_date, v.list.map((w) => Number(w.value)))}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                  <button
                                                    type="button"
                                                    style={{
                                                      color: "#FF0000",
                                                      position: "absolute",
                                                      top: "-16px",
                                                      right: "8px",
                                                      backgroundColor: "#FFFFFF",
                                                    }}
                                                    onClick={() => delList("cost", i)}
                                                    title="削除"
                                                  >
                                                    <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                                  </button>
                                                  {
                                                    v.list.map((w, j) => (
                                                      <div
                                                        style={{
                                                          width: "100%",
                                                          display: "flex",
                                                          justifyContent: "center",
                                                          gap: "8px",
                                                        }}
                                                      >
                                                        <input
                                                          type="text"
                                                          value={w.name || ""}
                                                          placeholder="項目名を入力"
                                                          onChange={(e) => changeInputDetailList("cost", "name", e.target.value, i, j)}
                                                          style={{
                                                            padding: "12px 16px",
                                                            textAlign: "right",
                                                            width: "100%",
                                                          }}
                                                        />
                                                        <input
                                                          type="text"
                                                          value={w.value || ""}
                                                          placeholder="金額(円)を入力"
                                                          onChange={(e) => changeInputDetailList("cost", "value", e.target.value, i, j)}
                                                          onBlur={() => formatInputDetailValue("cost", i, j)}
                                                          onKeyPress={(e) => e.key === "Enter" && formatInputDetailValue("cost", i, j)}
                                                          style={{
                                                            padding: "12px 16px",
                                                            textAlign: "right",
                                                            width: "100%",
                                                          }}
                                                        />
                                                        <button
                                                          type="button"
                                                          style={{
                                                            color: "#FF0000",
                                                          }}
                                                          onClick={() => delDetail("cost", i, j)}
                                                          title="削除"
                                                        >
                                                          <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                                        </button>
                                                      </div>
                                                    ))
                                                  }
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                    }}
                                                  >
                                                    <button
                                                      type="button"
                                                      onClick={() => addDetail("cost", i)}
                                                      style={{
                                                        backgroundColor: "#d6e8d4",
                                                        border: "1px solid #81b365",
                                                        color: "#81b365",
                                                        padding: "4px 16px",
                                                        borderRadius: "4px",
                                                        cursor: "pointer",
                                                      }}
                                                    >
                                                      <FontAwesomeIcon icon={['fas', 'plus']} />
                                                      &nbsp;
                                                      項目を追加
                                                    </button>
                                                  </div>
                                                </div>
                                              ))
                                            }
                                            {
                                              costErrorList.map((v) => (
                                                <div
                                                  className="error"
                                                  style={{
                                                    margin: "4px 0px",
                                                  }}
                                                >
                                                  {v}
                                                </div>
                                              ))
                                            }
                                            <div className="add_area">
                                              <button
                                                type="button"
                                                onClick={() => addList("cost")}
                                              >
                                                <FontAwesomeIcon icon={['fas', 'plus']} />
                                                &nbsp;
                                                その他のコスト追加
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </>
                                    )
                                  }
                                  {
                                    !login.permissions?.task_project_cost_setting && login.permissions?.task_cost_view && (
                                      <>
                                        <div className="item w100">
                                          <div
                                            className="title"
                                            style={{
                                              justifyContent: "space-between",
                                              marginBottom: inputAutoBudgets.length ? "16px" : "0",
                                            }}
                                          >
                                            <div>
                                              予算(円)
                                            </div>
                                            <div>
                                              {`${nowDate("YYYY年MM月DD日")}時点で確定している金額 計：`}
                                              {isNumber(totalBudgets) ? `${totalBudgets.toLocaleString()}円` : totalBudgets}
                                            </div>
                                          </div>
                                          <div
                                            className="detail row_1"
                                            style={{
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "8px",
                                            }}
                                          >
                                            {
                                              inputAutoBudgets.map((v, i) => (
                                                <div
                                                  key={`budget_${i.toString()}`}
                                                  style={{
                                                    position: "relative",
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    marginBottom: "8px",
                                                    marginTop: 0,
                                                    gap: "8px",
                                                    border: "solid 1px #999999",
                                                    borderRadius: "4px",
                                                    padding: "16px 8px",
                                                  }}
                                                >
                                                  <Tips
                                                    description="編集はプロジェクトコスト設定権限が必要です"
                                                    className=""
                                                    icon={false}
                                                  >
                                                    <div className="calendar_text">
                                                      <button
                                                        type="button"
                                                        className="display button"
                                                        style={{
                                                          backgroundColor: "#EEEEEE",
                                                          color: "#999999",
                                                          minWidth: "max-content",
                                                        }}
                                                      >
                                                        {format(v.start_date, "YYYY年M月")}
                                                      </button>
                                                    </div>
                                                  </Tips>
                                                  <div>
                                                    ~
                                                  </div>
                                                  <Tips
                                                    description="編集はプロジェクトコスト設定権限が必要です"
                                                    className=""
                                                    icon={false}
                                                  >
                                                    <div className="calendar_text">
                                                      <button
                                                        type="button"
                                                        className="display button"
                                                        style={{
                                                          backgroundColor: "#EEEEEE",
                                                          color: "#999999",
                                                          minWidth: "max-content",
                                                        }}
                                                      >
                                                        {v.end_date ? format(v.end_date, "YYYY年MM月") : "指定なし"}
                                                      </button>
                                                    </div>
                                                  </Tips>
                                                  <Tips
                                                    description="編集はプロジェクトコスト設定権限が必要です"
                                                    className=""
                                                    icon={false}
                                                    style={{
                                                      width: "calc(100% - 200px)",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        height: "auto",
                                                        width: "100%",
                                                        backgroundColor: "#EEEEEE",
                                                        color: "#999999",
                                                        border: "solid 1px #CCCCCC",
                                                        borderRadius: "4px",
                                                        fontSize: "14px",
                                                        padding: "10px 16px",
                                                        textAlign: "right",
                                                      }}
                                                    >
                                                      {`${Number(v.value || 0).toLocaleString()}円`}
                                                    </div>
                                                  </Tips>
                                                  <div
                                                    style={{
                                                      position: "absolute",
                                                      top: "-8px",
                                                      right: "50px",
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "0 8px",
                                                      fontSize: "10px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      {
                                                        v.end_date ? (
                                                          <>
                                                            {`期間:${calcDiffMonth(v.start_date, v.end_date)}ヶ月`}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {`期間未確定(確定分:${calcDiffMonth(v.start_date, v.end_date)}ヶ月)`}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                    <div>
                                                      {
                                                        isNumber(calcValue(v.start_date, v.end_date, [Number(v.value)])) ? (
                                                          <>
                                                            {`計:${calcValue(v.start_date, v.end_date, [Number(v.value)]).toLocaleString()}円`}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {calcValue(v.start_date, v.end_date, [Number(v.value)])}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                </div>
                                              ))
                                            }
                                            <Tips
                                              description="追加はプロジェクトコスト設定権限が必要です"
                                              className=""
                                              icon={false}
                                            >
                                              <div className="add_area">
                                                <button
                                                  type="button"
                                                  className="disabled"
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'plus']} />
                                                  &nbsp;
                                                  予算追加
                                                </button>
                                              </div>
                                            </Tips>
                                          </div>
                                        </div>
                                        <div className="item w100">
                                          <div
                                            className="title"
                                            style={{
                                              justifyContent: "space-between",
                                              marginBottom: inputAutoCosts.length ? "16px" : "0",
                                            }}
                                          >
                                            <div>
                                              その他のコスト(円)
                                            </div>
                                            <div>
                                              {`${nowDate("YYYY年MM月DD日")}時点で確定している金額 計：`}
                                              {isNumber(totalCost) ? `${totalCost.toLocaleString()}円` : totalCost}
                                            </div>
                                          </div>
                                          <div
                                            className="detail row_1"
                                            style={{
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "24px",
                                            }}
                                          >
                                            {
                                              inputAutoCosts.map((v, i) => (
                                                <div
                                                  key={`cost_${i.toString()}`}
                                                  style={{
                                                    position: "relative",
                                                    width: "100%",
                                                    display: "flex",
                                                    flexFlow: "column",
                                                    gap: "4px",
                                                    border: "solid 1px #CCCCCC",
                                                    borderRadius: "4px",
                                                    padding: "16px 8px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "8px",
                                                      position: "absolute",
                                                      top: "-14px",
                                                      left: "8px",
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "0 4px",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        width: "120px",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                      }}
                                                    >
                                                      <Tips
                                                        description="編集はプロジェクトコスト設定権限が必要です"
                                                        className=""
                                                        icon={false}
                                                        style={{
                                                          width: "100%",
                                                        }}
                                                      >
                                                        <div
                                                          style={{
                                                            position: "relative",
                                                            border: "solid 1px #CCCCCC",
                                                            borderRadius: "4px",
                                                            width: "100%",
                                                            padding: "4px",
                                                            textAlign: "center",
                                                            fontSize: "14px",
                                                            backgroundColor: "#EEEEEE",
                                                            color: "#999999",
                                                          }}
                                                        >
                                                          {format(v.start_date, "YYYY年MM月")}
                                                        </div>
                                                      </Tips>
                                                    </div>
                                                    <div>
                                                      ~
                                                    </div>
                                                    <div
                                                      style={{
                                                        width: "120px",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                      }}
                                                    >
                                                      <Tips
                                                        description="編集はプロジェクトコスト設定権限が必要です"
                                                        className=""
                                                        icon={false}
                                                        style={{
                                                          width: "100%",
                                                        }}
                                                      >
                                                        <div
                                                          style={{
                                                            position: "relative",
                                                            border: "solid 1px #CCCCCC",
                                                            borderRadius: "4px",
                                                            width: "100%",
                                                            padding: "4px",
                                                            textAlign: "center",
                                                            fontSize: "14px",
                                                            backgroundColor: "#EEEEEE",
                                                            color: "#999999",
                                                          }}
                                                        >
                                                          {v.end_date ? format(v.end_date, "YYYY年MM月") : "指定なし"}
                                                        </div>
                                                      </Tips>
                                                    </div>
                                                  </div>
                                                  <div
                                                    style={{
                                                      position: "absolute",
                                                      top: "-8px",
                                                      right: "50px",
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "0 8px",
                                                      fontSize: "10px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      {
                                                        v.end_date ? (
                                                          <>
                                                            {`期間:${calcDiffMonth(v.start_date, v.end_date)}ヶ月`}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {`期間未確定(確定分:${calcDiffMonth(v.start_date, v.end_date)}ヶ月)`}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                    <div>
                                                      {
                                                        isNumber(calcValue(v.start_date, v.end_date, v.list.map((w) => Number(w.value)))) ? (
                                                          <>
                                                            {/* eslint-disable-next-line max-len */}
                                                            {`計:${calcValue(v.start_date, v.end_date, v.list.map((w) => Number(w.value))).toLocaleString()}円`}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {calcValue(v.start_date, v.end_date, v.list.map((w) => Number(w.value)))}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                  {
                                                    v.list.map((w) => (
                                                      <div
                                                        style={{
                                                          width: "100%",
                                                          display: "flex",
                                                          justifyContent: "center",
                                                          gap: "8px",
                                                        }}
                                                      >
                                                        <Tips
                                                          description="編集はプロジェクトコスト設定権限が必要です"
                                                          className=""
                                                          icon={false}
                                                          style={{
                                                            width: "100%",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              height: "auto",
                                                              width: "100%",
                                                              backgroundColor: "#EEEEEE",
                                                              color: "#999999",
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              fontSize: "14px",
                                                              padding: "10px 16px",
                                                              textAlign: "right",
                                                              minHeight: "38px",
                                                            }}
                                                          >
                                                            {w.name || "項目名なし"}
                                                          </div>
                                                        </Tips>
                                                        <Tips
                                                          description="編集はプロジェクトコスト設定権限が必要です"
                                                          className=""
                                                          icon={false}
                                                          style={{
                                                            width: "100%",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              height: "auto",
                                                              width: "100%",
                                                              backgroundColor: "#EEEEEE",
                                                              color: "#999999",
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              fontSize: "14px",
                                                              padding: "10px 16px",
                                                              textAlign: "right",
                                                            }}
                                                          >
                                                            {`${Number(w.value || 0).toLocaleString()}円`}
                                                          </div>
                                                        </Tips>
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              ))
                                            }
                                            <Tips
                                              description="追加はプロジェクトコスト設定権限が必要です"
                                              className=""
                                              icon={false}
                                            >
                                              <div className="add_area">
                                                <button
                                                  type="button"
                                                  className="disabled"
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'plus']} />
                                                  &nbsp;
                                                  その他のコスト追加
                                                </button>
                                              </div>
                                            </Tips>
                                          </div>
                                        </div>
                                      </>
                                    )
                                  }
                                  <div className="item w100">
                                    <div
                                      className="title"
                                      style={{
                                        justifyContent: "space-between",
                                        marginBottom: inputAutoScheduledTimes.length ? "16px" : "0",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                        }}
                                      >
                                        予定(時間)
                                      </div>
                                      <div>
                                        {`${nowDate("YYYY年MM月DD日")}時点で確定している時間 計：`}
                                        {
                                          isNumber(totalScheduledTime) ? (
                                            <>
                                              {`${Math.floor(totalScheduledTime / 3600)}時間`}
                                              {
                                                totalScheduledTime % 3600 ? (
                                                  <>
                                                    {`${(totalScheduledTime % 3600) / 60}分`}
                                                  </>
                                                ) : (
                                                  <></>
                                                )
                                              }
                                            </>
                                          ) : (
                                            <>
                                              {totalScheduledTime}
                                            </>
                                          )
                                        }
                                      </div>
                                    </div>
                                    <div
                                      className="detail row_1"
                                      style={{
                                        display: "flex",
                                        flexFlow: "column",
                                        gap: "8px",
                                      }}
                                    >
                                      {
                                        inputAutoScheduledTimes.map((v, i) => (
                                          <div
                                            key={`scheduled_time__${i.toString()}`}
                                            style={{
                                              position: "relative",
                                              width: "100%",
                                              display: "flex",
                                              alignItems: "center",
                                              marginBottom: "8px",
                                              marginTop: 0,
                                              gap: "8px",
                                              border: "solid 1px #999999",
                                              borderRadius: "4px",
                                              padding: "16px 8px",
                                            }}
                                          >
                                            <DateModule
                                              date={v.start_date}
                                              changeCallback={(callback_date) => changeInputList("scheduled_time", "start_date", callback_date, i)}
                                              noLimit
                                              navigation={false}
                                            />
                                            <div>
                                              ~
                                            </div>
                                            <DateModule
                                              date={v.end_date}
                                              changeCallback={(callback_date) => changeInputList("scheduled_time", "end_date", callback_date, i)}
                                              noLimit
                                              navigation={false}
                                              empty
                                            />
                                            <input
                                              type="text"
                                              value={v.value || ""}
                                              placeholder="予定(時間)を入力"
                                              onChange={(e) => changeInputList("scheduled_time", "value", e.target.value, i)}
                                              onBlur={() => formatInputValue("scheduled_time", i)}
                                              onKeyPress={(e) => e.key === "Enter" && formatInputValue("scheduled_time", i)}
                                              style={{
                                                padding: "12px 16px",
                                                textAlign: "right",
                                              }}
                                            />
                                            <Tips
                                              description="予定を削除"
                                              className="del"
                                              icon={false}
                                            >
                                              <button
                                                type="button"
                                                onClick={() => delList("scheduled_time", i)}
                                              >
                                                <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                              </button>
                                            </Tips>
                                            <div
                                              style={{
                                                position: "absolute",
                                                top: "-8px",
                                                right: "50px",
                                                backgroundColor: "#FFFFFF",
                                                padding: "0 8px",
                                                fontSize: "10px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                              }}
                                            >
                                              <div>
                                                {
                                                  v.end_date ? (
                                                    <>
                                                      {`期間:${calcDiffMonth(v.start_date, v.end_date)}ヶ月`}
                                                    </>
                                                  ) : (
                                                    <>
                                                      {`期間未確定(確定分:${calcDiffMonth(v.start_date, v.end_date)}ヶ月)`}
                                                    </>
                                                  )
                                                }
                                              </div>
                                              <div>
                                                {
                                                  isNumber(calcValue(v.start_date, v.end_date, [Number(v.value)])) ? (
                                                    <>
                                                      {`計:${calcValue(v.start_date, v.end_date, [Number(v.value)]).toLocaleString()}時間`}
                                                    </>
                                                  ) : (
                                                    <>
                                                      {calcValue(v.start_date, v.end_date, [Number(v.value)])}
                                                    </>
                                                  )
                                                }
                                              </div>
                                            </div>
                                          </div>
                                        ))
                                      }
                                      {
                                        scheduleTimeErrorList.map((v) => (
                                          <div
                                            className="error"
                                            style={{
                                              margin: "4px 0px",
                                            }}
                                          >
                                            {v}
                                          </div>
                                        ))
                                      }
                                      <div className="add_area">
                                        <button
                                          type="button"
                                          onClick={() => addList("scheduled_time")}
                                        >
                                          <FontAwesomeIcon icon={['fas', 'plus']} />
                                          &nbsp;
                                          予定追加
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )
                            }
                          </fieldset>
                        </>
                      )
                    }
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "8px",
                        gap: "8px",
                      }}
                    >
                      <button
                        type="button"
                        className="button cancel"
                        onClick={() => history.go(-1)}
                      >
                        キャンセル
                      </button>
                      <button
                        type="button"
                        className="button"
                        onClick={addLabelGroup}
                        disabled={
                          currentList.includes(inputGroupName)
                          || !inputGroupName
                          || !!labelsError
                          || !inputLabels.filter((v) => v.label_name).length
                          || (!selectUseTeams.length && !selectUseMembers.length)
                          || (!selectViewTeams.length && !selectViewMembers.length)
                          || !!scheduleTimeErrorList.length
                          || (
                            isDisplayCostArea
                            && (
                              (!!budgetErrorList.length)
                              || (!!costErrorList.length)
                            )
                          )
                          || isSubmit
                        }
                      >
                        {id ? "更新" : "作成"}
                      </button>
                    </div>
                  </div>
                </>
              )
            }
          </div>
        </div>
      </section>
      {/* 作業作成モーダル */}
      <AddLabelModal
        modalState={modalState}
        closeCallback={closeAddLabel}
        addCallback={(params) => addLabel(params)}
        isDisplay={isDisplayAddLabel}
        tasks={[...task.taskLabelSettingDetail?.label_masters.filter((v) => !inputLabels.map((w) => w.label_name).includes(v.name)) || []]}
        currentTasks={inputLabels.map((v) => v.label_name || "")}
        teamList={selectUseTeams}
        memberList={selectUseMembers}
      />
      <Prompt
        when={isEdit}
        message="このページから移動しますか？行った変更が保存されない可能性があります。"
      />
    </section>
  )
}

export default Detail
